import { apiClient } from "@/services/api"
import { defineStore } from "pinia"
import { DUE_FILTERS, dueFilterParam, isKnownFileType, SORT_KEYS } from "@/services/fileBrowser"

// State for the global file browser (`GET /files/...`).
//
// One listing is one opened folder: what it is, the path back out, its folders and one page of its
// files. Listings are cached per location, per ordering *and per due filter*, because all three
// decide which rows a page contains — walking back into a folder you have already opened costs
// nothing, changing the sort or the filter refetches, which is the only correct thing to do with
// server-side paging.
//
// Search results are held separately from folder contents: they are a different list with different
// rows (each names the folder it lives in), not a filtered view of one folder.

const headers = { tenant: true, authorization: true }

/** Matches `DomainFileQueries.MAX_PAGE_SIZE`; asking for more is silently capped server-side. */
export const FILES_PER_PAGE = 50

/** The root of the hierarchy — the place list. Mirrors the backend's `FolderPath.Root`. */
export const ROOT_PATH = ""

const listingKey = (path, sort, ascending, due = DUE_FILTERS.ALL) =>
  `${path}|${sort}|${ascending ? "asc" : "desc"}|${due}`

/** The permissions request while it is in flight — see `loadPermissions`. */
let permissionsRequest = null

/** Drops rows of a type this build does not know, so a backend that grows a ninth type degrades quietly. */
const known = rows => (rows ?? []).filter(row => isKnownFileType(row.type))

export const useFileBrowserStore = defineStore("fileBrowser", {
  state: () => ({
    /** `{ "path|sort|dir": { …listing, page } }` */
    listings: {},
    /** A search answers with both: records whose name matches, then files whose name or tag does. */
    searchFolders: [],
    searchResults: [],
    /** Which types the caller may retag or upload to, keyed by type — from `GET /files/permissions`. */
    writableTypes: {},
    /**
     * The content types an upload may have, as the server's own validator lists them. Sent rather
     * than mirrored here: a second copy would drift, and the drift would show as a file picker
     * offering files the upload then refuses.
     */
    allowedContentTypes: [],
    /**
     * The largest an upload may be, in bytes — `spring.servlet.multipart.max-file-size` as the server
     * itself reads it. Sent rather than mirrored for the same reason as [allowedContentTypes], with a
     * sharper failure: a stale copy either turns away files the server would have taken, or lets a
     * user wait out a long upload for a rejection that (past Tomcat's 2 MB swallow limit) arrives as a
     * network error rather than a 413.
     *
     * `null` until the fetch lands, which every reader treats as "no limit known" — the server still
     * gates it.
     */
    maxUploadBytes: null,
    /**
     * The workspace's own due-date thresholds — how many days before a document's due date it starts
     * warning, and how many before it turns critical. Sent with the permissions for the same reason
     * as the two limits above: they are the numbers the notification monitor raises its alert from,
     * and a constant here would be a second copy of something an admin can change.
     *
     * `null` until the fetch lands, which `useFileDueStatus` treats as "draw no badge yet" rather
     * than guessing at a default and redrawing every row a moment later.
     */
    documentDueWarningDays: null,
    documentDueCriticalDays: null,
    permissionsLoaded: false,
    /**
     * What the workspace's storage allowance is spent on — `{ usedBytes, maxBytes, availableBytes }`
     * from `GET /files/storage`, or `null` until it lands.
     *
     * Null rather than a zeroed triple, because `0 B von 0 B` is a readable sentence that says
     * something false: it reads as an allowance of nothing rather than as an answer that has not
     * arrived. Every reader draws nothing until there is something to draw.
     *
     * Held apart from [permissionsLoaded]'s one-shot fetch: permissions are fixed for the session,
     * this moves with every upload.
     */
    storage: null,
  }),
  getters: {
    getListing: state => (path, sort, ascending, due) => state.listings[listingKey(path, sort, ascending, due)] ?? null,
    getSearchFolders: state => state.searchFolders,
    getSearchResults: state => state.searchResults,
    canWrite: state => type => state.writableTypes[type] === true,
    getStorage: state => state.storage,
    getAcceptedContentTypes: state => state.allowedContentTypes.join(","),
  },
  actions: {
    reset() {
      this.listings = {}
      this.searchFolders = []
      this.searchResults = []
    },

    /**
     * Forgets the permissions and limits too, not just what `reset` drops. They are fetched once per
     * session and belong to the person and workspace that signed in, so a session change (the auth
     * store's `clearSessionData`) must drop them — and disown a request still on its way.
     */
    clearSession() {
      permissionsRequest = null
      this.$reset()
    },

    /**
     * Opens a folder, or appends the next page of the one already open.
     *
     * @param reset drop what is cached for this location first — used after an edit.
     */
    loadListing({
      path = ROOT_PATH,
      sort = SORT_KEYS.NAME,
      ascending = true,
      due = DUE_FILTERS.ALL,
      next = false,
      reset = false,
    }) {
      const key = listingKey(path, sort, ascending, due)
      const existing = this.listings[key]
      if (existing && !next && !reset) return Promise.resolve(existing)
      if (next && (!existing || !existing.hasMore)) return Promise.resolve(existing ?? null)

      const page = next && existing ? existing.page + 1 : 0

      return apiClient
        .get("/files/browse", {
          params: { path, sort, ascending, due: dueFilterParam(due), page, itemsPerPage: FILES_PER_PAGE },
          headers,
        })
        .then(res => {
          const incoming = res.data ?? {}
          const previous = page === 0 ? null : this.listings[key]

          this.listings[key] = {
            ...incoming,
            // Past page 0 the server sends only the list it is paging; the other one is already here.
            folders: [...(previous?.folders ?? []), ...known(incoming.folders)],
            files: [...(previous?.files ?? []), ...known(incoming.files)],
            page,
          }
          return this.listings[key]
        })
    },

    /**
     * Refetches what a write to [path] just invalidated.
     *
     * The folder's own contents and totals, and the places list, whose counts and running total both
     * include whatever was added. Other cached orderings of the same folder are dropped rather than
     * refetched: they are only paid for if the user switches back to one, and leaving them would show
     * a page that predates the upload.
     */
    refreshAfterWrite({ path, sort = SORT_KEYS.NAME, ascending = true, due = DUE_FILTERS.ALL }) {
      const keep = listingKey(path, sort, ascending, due)
      Object.keys(this.listings).forEach(key => {
        if (key.startsWith(`${path}|`) && key !== keep) delete this.listings[key]
      })

      return Promise.all([
        this.loadListing({ path, sort, ascending, due, reset: true }),
        this.loadListing({ path: ROOT_PATH, sort: SORT_KEYS.NAME, ascending: true, due, reset: true }),
        // The allowance is what the write spent, so it is as stale as the folder is.
        this.loadStorage(),
      ])
    },

    /**
     * @param sort applied server-side, before the result cap. Reordering the rows here instead would
     *   answer a different question — not "the first fifty by name" but "the fifty newest, arranged
     *   by name", which looks the same and is not.
     */
    search(q, { sort = SORT_KEYS.MODIFIED, ascending = false, due = DUE_FILTERS.ALL } = {}) {
      const params = { q, sort, ascending, due: dueFilterParam(due) }
      return apiClient.get("/files/search", { params, headers }).then(res => {
        this.searchFolders = known(res.data?.folders)
        this.searchResults = known(res.data?.files)
        return res
      })
    },

    clearSearch() {
      this.searchFolders = []
      this.searchResults = []
    },

    /**
     * Fetched once per session, and once even while in flight.
     *
     * `permissionsLoaded` only goes up when the response lands, so a guard on it alone deduplicates
     * nothing during the round trip — which was harmless while the only callers were the two ponds,
     * and is not now that every file row asks for the due-date thresholds through it. Fifty rows
     * would be fifty identical requests on first paint. Holding the promise makes the second caller
     * wait for the first one's answer instead of asking again; it is dropped on failure so a retry
     * is still possible.
     */
    loadPermissions() {
      if (this.permissionsLoaded) return Promise.resolve()
      if (permissionsRequest) return permissionsRequest

      const request = apiClient
        .get("/files/permissions", { headers })
        .then(res => {
          // Disowned by `clearSession` on the way: the answer belongs to the session that just ended.
          if (permissionsRequest !== request) return res
          this.writableTypes = res.data?.writable ?? {}
          this.allowedContentTypes = res.data?.allowedContentTypes ?? []
          this.maxUploadBytes = res.data?.maxUploadBytes ?? null
          this.documentDueWarningDays = res.data?.documentDueWarningDays ?? null
          this.documentDueCriticalDays = res.data?.documentDueCriticalDays ?? null
          this.permissionsLoaded = true
          return res
        })
        .finally(() => {
          if (permissionsRequest === request) permissionsRequest = null
        })
      permissionsRequest = request

      return request
    },

    /**
     * How much of the workspace's allowance is used, and how much is left.
     *
     * Refetched rather than adjusted by the size of what was just uploaded: the server's counter is
     * the one the upload gate reads, it also moves when someone else uploads, and files stored under
     * an ignored space (a profile picture, the company logo) never enter it at all — so arithmetic
     * here would drift away from the number that actually decides whether the next upload is taken.
     *
     * Failures are swallowed: the meter is an aside on a screen whose job is listing files, and the
     * interceptor has already said whatever there was to say.
     */
    loadStorage() {
      return apiClient
        .get("/files/storage", { headers })
        .then(res => {
          this.storage = res.data ?? null
          return this.storage
        })
        .catch(() => null)
    },

    /**
     * Changes one file's metadata and writes the returned row back into every list showing it.
     *
     * A file appears in at most one folder, but that folder may be cached under several orderings,
     * and it may be on screen as a search hit at the same time — so every cached copy is patched
     * rather than the one the edit happened to come from.
     *
     * Tags, due date and lead go in one request because they are one save: the dialog sends its whole
     * state, so an absent value means the field was cleared. The document date follows the same rule:
     * empty means "the same as the upload". Only an SPA too old to send it at all leaves it alone.
     */
    updateFileMeta({ type, ownerId, fileId, tags, dueDate, dueLeadDays, documentDate }) {
      const body = {
        tags,
        dueDate: dueDate || null,
        dueLeadDays: dueLeadDays ?? null,
        documentDate: documentDate || null,
      }
      return apiClient.put(`/files/${type}/${ownerId}/${fileId}`, body, { headers }).then(res => {
        const updated = res.data
        const matches = file => file.id === fileId && file.ownerId === ownerId

        Object.values(this.listings).forEach(listing => {
          const index = listing.files.findIndex(matches)
          if (index >= 0) listing.files.splice(index, 1, updated)
        })
        const hit = this.searchResults.findIndex(matches)
        if (hit >= 0) this.searchResults.splice(hit, 1, updated)

        return updated
      })
    },
  },
})
